/* A3 — Números e instâncias (Painel Administrativo).
 *
 * Cada empresa pode ter vários números; cada número é uma instância no servidor
 * Evolution dela. Ligada ao endpoint real `/api/numbers` (Fase 0). O papel no
 * hub é editável e salva de verdade.
 *
 * REAL: empresa · instância · telefone · estado da conexão · servidor · papel
 *   no hub (editável) · proxy (editável — Fase D, passo 5).
 * FASE 4: o roteamento/balanceamento em si (rodízio entre números, lead
 *   atrelado, aviso de troca). Aqui se define o papel, o proxy e vê o estado.
 *
 * O proxy é infraestrutura, e por isso mora AQUI e não no painel do cliente: o
 * cliente vê "celular conectado"; quem decide por qual IP ele sai é a
 * plataforma. Salvar aplica na instância na hora (ou na criação dela, se ainda
 * não pareou) — antes de existir esta tela, o campo só acendia um selo. */

import React, { useEffect, useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { relativo } from './formato';
import { Aviso, Button, Cabecalho, card, Icon, IconButton, Nota, Secao, Select, Tag } from './ui';

function estadoDaConexao(estado) {
  if (estado === 'open') return { tone: 'success', rotulo: 'Conectado' };
  if (estado === 'connecting') return { tone: 'warning', rotulo: 'Conectando' };
  return { tone: 'danger', rotulo: 'Desconectado' };
}

const HUB = [
  { value: 'ativo', label: 'Ativo no rodízio' },
  { value: 'reserva', label: 'Reserva' },
  { value: 'fora', label: 'Fora do rodízio' },
];

const th = {
  position: 'sticky', top: 0, zIndex: 1, textAlign: 'left', padding: '12px 20px',
  background: 'var(--surface-card)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)',
  color: 'var(--text-muted)', letterSpacing: 'var(--ls-wide)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
};
const td = { padding: '12px 20px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' };

export default function Numeros() {
  const recurso = useRecurso((sinal) => api.numeros(sinal), []);
  const numeros = recurso.dados?.itens || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho titulo="Números" descricao="Os números de WhatsApp de cada empresa, o estado de cada instância e o papel no rodízio." />

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px' }}>
        <Secao titulo={recurso.carregando ? 'Carregando…' : `${numeros.length} ${numeros.length === 1 ? 'número' : 'números'}`}>
          {recurso.erro ? (
            <Aviso titulo="Os números não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
          ) : !recurso.carregando && numeros.length === 0 ? (
            <Aviso titulo="Nenhum número conectado" texto="Conecte o primeiro número de uma empresa para começar a atender." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr>
                  <th style={th}>Empresa</th><th style={th}>Instância</th><th style={th}>Estado</th>
                  <th style={{ ...th, textAlign: 'right' }}>Verificado</th><th style={th}>Papel no hub</th><th style={th}>Proxy</th>
                </tr>
              </thead>
              <tbody>
                {numeros.map((n) => <Linha key={n.numero_id} numero={n} aoMudar={recurso.recarregar} />)}
              </tbody>
            </table>
          )}
        </Secao>
        <Nota>Fonte: /api/numbers. O proxy vale a partir da próxima conexão da sessão: um número já pareado continua no caminho antigo até reconectar. O balanceamento em si (rodízio, lead atrelado, aviso de troca) chega na Fase 4.</Nota>
      </div>
    </div>
  );
}

function Linha({ numero, aoMudar }) {
  const [papel, setPapel] = useState(numero.papel_no_hub || 'ativo');
  const [salvando, setSalvando] = useState(false);
  const [editandoProxy, setEditandoProxy] = useState(false);
  useEffect(() => { setPapel(numero.papel_no_hub || 'ativo'); }, [numero]);

  const est = estadoDaConexao(numero.conexao);

  async function mudarPapel(valor) {
    setPapel(valor);
    setSalvando(true);
    try {
      await api.salvarNumero(numero.numero_id, { papel_no_hub: valor });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <tr style={salvando ? { opacity: 0.7 } : undefined}>
      <td style={{ ...td, color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--text-muted)' }}><Icon name="smartphone" size={14} /></span>
          {numero.empresa}
        </span>
      </td>
      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-label)' }}>{numero.instancia || '—'}</td>
      <td style={td}><Tag tone={est.tone} dot>{est.rotulo}</Tag></td>
      <td className="nb-tab" style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{relativo(numero.verificado_em)}</td>
      <td style={{ ...td, maxWidth: 200 }}>
        <Select value={papel} onChange={(e) => mudarPapel(e.target.value)} options={HUB} />
      </td>
      <td style={td}>
        <button
          type="button"
          onClick={() => setEditandoProxy(true)}
          title={numero.tem_proxy ? 'Editar o proxy deste número' : 'Definir um proxy para este número'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}
        >
          <Tag tone={numero.tem_proxy ? 'info' : 'neutral'}>{numero.tem_proxy ? 'Com proxy' : 'Sem proxy'}</Tag>
          <span style={{ color: 'var(--text-muted)', display: 'inline-flex' }}><Icon name="server" size={13} /></span>
        </button>
        {editandoProxy && (
          <EditorDeProxy
            numero={numero}
            aoFechar={(mudou) => { setEditandoProxy(false); if (mudou) aoMudar?.(); }}
          />
        )}
      </td>
    </tr>
  );
}

const PROTOCOLOS = [
  { value: 'http', label: 'HTTP' },
  { value: 'https', label: 'HTTPS' },
  { value: 'socks4', label: 'SOCKS4' },
  { value: 'socks5', label: 'SOCKS5' },
];

const inp = { height: 40, padding: '0 12px', width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' };
const rotulo = { font: 'var(--type-label)', color: 'var(--text-secondary)' };
const dica = { font: 'var(--type-caption)', color: 'var(--text-muted)' };

/** Edita o proxy de UM número.
 *
 *  Lê o estado atual da instância (host, porta, protocolo, usuário — a senha
 *  nunca volta, só `senha_preenchida`). Salva pelo cadastro do número
 *  (`PUT /api/numbers/<id>` com `proxy_url`), e não pela rota da instância:
 *  é o caminho que funciona ANTES de parear — o proxy fica guardado e é
 *  aplicado quando a instância for criada. Com a instância no ar, o backend
 *  aplica primeiro e só grava se a Evolution aceitar. */
function EditorDeProxy({ numero, aoFechar }) {
  const [lendo, setLendo] = useState(true);
  const [ativo, setAtivo] = useState(Boolean(numero.tem_proxy));
  const [protocolo, setProtocolo] = useState('socks5');
  const [host, setHost] = useState('');
  const [porta, setPorta] = useState('');
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaPreenchida, setSenhaPreenchida] = useState(false);
  const [semLeitura, setSemLeitura] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const atual = await api.proxyDaInstancia(numero.numero_id);
        if (!vivo) return;
        setAtivo(Boolean(atual.ativo));
        setProtocolo(atual.protocolo || 'socks5');
        setHost(atual.host || '');
        setPorta(atual.porta || '');
        setUsuario(atual.usuario || '');
        setSenhaPreenchida(Boolean(atual.senha_preenchida));
      } catch {
        // Sem instância ainda, ou Evolution fora do ar: dá para definir mesmo
        // assim — o proxy é aplicado quando a instância nascer.
        if (vivo) setSemLeitura(true);
      } finally {
        if (vivo) setLendo(false);
      }
    })();
    return () => { vivo = false; };
  }, [numero.numero_id]);

  // A mesma forma canônica que o backend grava (`_gravar_proxy`): credenciais
  // escapadas, porque uma senha com `@` ou `:` quebraria a leitura de volta.
  function montarUrl() {
    if (!ativo) return '';
    const cred = (usuario || senha) ? `${encodeURIComponent(usuario)}:${encodeURIComponent(senha)}@` : '';
    return `${protocolo}://${cred}${host.trim()}:${String(porta).trim()}`;
  }

  async function salvar() {
    setErro(null);
    if (ativo) {
      if (!host.trim()) { setErro('Informe o host do proxy.'); return; }
      if (!/^\d+$/.test(String(porta).trim())) { setErro('Informe a porta do proxy (1 a 65535).'); return; }
      if (usuario && !senha) {
        // A senha não é exibida nem reaproveitada — é write-only, como as
        // chaves. Trocar o proxy é informá-la de novo.
        setErro('Informe a senha do proxy: ela não é reaproveitada da configuração anterior.');
        return;
      }
    }
    setSalvando(true);
    try {
      await api.salvarNumero(numero.numero_id, { proxy_url: montarUrl() });
      aoFechar(true);
    } catch (e) {
      setErro(e?.mensagem || e?.message || 'Não foi possível salvar o proxy.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,5,20,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 6 }}>
      <div style={{ ...card, width: 460, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>Proxy do número</h3>
            <span style={{ ...dica, fontFamily: 'var(--font-mono)' }}>{numero.instancia || `#${numero.numero_id}`}{numero.telefone ? ` · ${numero.telefone}` : ''}</span>
          </div>
          <IconButton icon="x" label="Fechar" onClick={() => aoFechar(false)} />
        </header>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {lendo ? (
            <span style={dica}>Lendo o proxy atual da instância…</span>
          ) : (
            <>
              {semLeitura && (
                <span style={dica}>Não deu para ler o proxy atual (a instância ainda não existe, ou a Evolution não respondeu). Você pode definir mesmo assim: ele é aplicado quando o número parear.</span>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
                <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-primary)' }}>Este número sai por um proxy próprio</span>
              </label>

              {ativo && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 110px', gap: 10 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={rotulo}>Protocolo</span>
                      <Select value={protocolo} onChange={(e) => setProtocolo(e.target.value)} options={PROTOCOLOS} fullWidth />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={rotulo}>Host</span>
                      <input style={inp} value={host} onChange={(e) => setHost(e.target.value)} placeholder="proxy.exemplo.com.br" autoComplete="off" />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={rotulo}>Porta</span>
                      <input style={inp} inputMode="numeric" value={porta} onChange={(e) => setPorta(e.target.value)} placeholder="1080" />
                    </label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={rotulo}>Usuário <span style={{ color: 'var(--text-muted)' }}>(opcional)</span></span>
                      <input style={inp} value={usuario} onChange={(e) => setUsuario(e.target.value)} autoComplete="off" />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={rotulo}>Senha</span>
                      <input type="password" style={inp} value={senha} onChange={(e) => setSenha(e.target.value)} placeholder={senhaPreenchida ? 'Informe de novo para salvar' : ''} autoComplete="new-password" />
                    </label>
                  </div>
                  <span style={dica}>A senha nunca é exibida nem reaproveitada — para trocar qualquer campo, informe-a de novo. Só o host, sem esquema nem porta; o esquema vai no protocolo.</span>
                </>
              )}

              {!ativo && numero.tem_proxy && (
                <span style={dica}>Ao salvar, o proxy é desligado na instância e removido do cadastro.</span>
              )}

              {erro && (
                <div style={{ padding: '10px 12px', background: 'var(--danger-soft, rgba(220,60,60,.12))', borderRadius: 'var(--radius-control)', font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>{erro}</div>
              )}
            </>
          )}
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          <Button variant="ghost" onClick={() => aoFechar(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="primary" onClick={salvar} disabled={lendo || salvando}>{salvando ? 'Aplicando…' : ativo ? 'Aplicar proxy' : 'Desligar proxy'}</Button>
        </footer>
      </div>
    </div>
  );
}
